import { useMemo } from "react";
import { Clock } from "lucide-react";
import { Business, Bundle, Service } from "@/types/booking";
import { Button } from "@/components/ui/button";
import { formatDuration, formatPrice, getBusinessCurrency } from "@/lib/utils";

function getBundleDisplayPrice(bundle: Bundle): number {
  if (bundle?.usesCustomPrice && typeof bundle.customPrice === "number") {
    return bundle.customPrice;
  }
  if (typeof bundle.discountPercentage === "number" && bundle.discountPercentage > 0) {
    return (
      Math.round(
        (bundle.totalPrice * (1 - bundle.discountPercentage / 100) + Number.EPSILON) * 100,
      ) / 100
    );
  }
  return bundle.totalPrice;
}

function bundleServicesToString(bundle: Bundle): string {
  const items = Array.isArray(bundle.bundleServices) ? bundle.bundleServices : [];
  const parts = items
    .filter((row) => row?.service?.name)
    .map((row) => `${row.quantity || 1}x ${row.service?.name}`);
  return parts.join(", ");
}

function getBundleTotalDurationMinutes(bundle: Bundle, services: Service[] = []): number {
  const rows = Array.isArray(bundle.bundleServices) ? bundle.bundleServices : [];
  const durations = rows.map((row) => {
    const service =
      services.find((item) => item.id === row?.serviceId || item.id === row?.service?.id) ||
      row?.service;
    const unit = (service?.duration || 0) + (service?.extraTime || 0);
    const qty = row?.quantity || 1;
    return unit * qty;
  });

  const scheduleType = String(bundle?.scheduleType || "sequence").toLowerCase();
  if (scheduleType === "parallel") return durations.reduce((m, d) => Math.max(m, d), 0);
  return durations.reduce((sum, d) => sum + d, 0);
}

export default function BundleTile({
  bundle,
  businessInfo,
  services = [],
  selected,
  onSelect,
}: {
  bundle: Bundle;
  businessInfo: Business | null;
  services?: Service[];
  selected: boolean;
  onSelect: () => void;
}) {
  const currency = getBusinessCurrency(businessInfo);
  const displayPrice = getBundleDisplayPrice(bundle);
  const totalDurationMinutes = useMemo(
    () => getBundleTotalDurationMinutes(bundle, services),
    [bundle, services],
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-grey-900 mb-1 font-heading truncate">
            {bundle.name}
          </h3>

          <p className="text-grey-600 text-sm leading-relaxed mt-2">
            {bundle.description || bundleServicesToString(bundle) || "Bundle"}
          </p>

          {totalDurationMinutes > 0 && (
            <div className="flex items-center gap-4 text-sm mt-3">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-grey-400" />
                <span className="text-grey-600 font-medium">
                  {formatDuration(totalDurationMinutes)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="text-right ml-4">
          {displayPrice < bundle.totalPrice && (
            <p className="text-xs text-grey-400 line-through">
              {formatPrice(bundle.totalPrice, currency)}
            </p>
          )}
          <p className="text-lg font-semibold text-green-300">
            {formatPrice(displayPrice, currency)}
          </p>
        </div>
      </div>

      <Button
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className={
          selected
            ? "w-full bg-green-300 text-white"
            : "w-full border-green-300 text-green-300 hover:bg-green-300 hover:text-white transition-colors"
        }
        variant={selected ? "default" : "outline"}
      >
        {selected ? "Selected" : "Select Bundle"}
      </Button>
    </div>
  );
}
